import { useMemo } from 'react';
import { motion } from 'framer-motion';

/**
 * TradingChartBackground
 *
 * Subtle animated trading-chart visual used behind hero sections (e.g. /join).
 * Renders:
 *   - faint grid pattern
 *   - 2 animated SVG line-chart traces (drift L→R)
 *   - drifting candlestick column row
 *   - soft Y-axis price ticks for realism
 *
 * Notes:
 *   - All elements have low opacity / blur so foreground text stays readable.
 *   - Uses `transform` only animations for GPU performance.
 *   - Pointer-events disabled so it never intercepts clicks.
 */
export default function TradingChartBackground({ className = '' }) {
  // Deterministic candlestick column data
  const candles = useMemo(() => {
    const out = [];
    let price = 50;
    for (let i = 0; i < 24; i++) {
      const change = (Math.sin(i * 1.7) + Math.cos(i * 0.6)) * 6 + (Math.random() - 0.5) * 3;
      const open = price;
      price = Math.max(15, Math.min(85, price + change));
      const close = price;
      const high = Math.max(open, close) + Math.random() * 4 + 1;
      const low = Math.min(open, close) - Math.random() * 4 - 1;
      out.push({ open, close, high, low, bull: close >= open });
    }
    return out;
  }, []);

  // Build smooth chart line path (sin-mix)
  function makeLine(seed = 0, points = 60, baseY = 50, amp = 18) {
    const parts = [];
    for (let i = 0; i < points; i++) {
      const x = (i / (points - 1)) * 200;
      const y =
        baseY +
        Math.sin(seed + i * 0.18) * amp * 0.6 +
        Math.cos(seed * 0.7 + i * 0.32) * amp * 0.4;
      parts.push(`${x.toFixed(2)},${y.toFixed(2)}`);
    }
    return parts.join(' ');
  }

  const line1 = useMemo(() => makeLine(1.3, 60, 52, 16), []);
  const line2 = useMemo(() => makeLine(3.7, 60, 60, 12), []);
  const line3 = useMemo(() => makeLine(5.1, 60, 44, 10), []);

  return (
    <div
      aria-hidden="true"
      className={'pointer-events-none absolute inset-0 overflow-hidden ' + className}
      data-testid="trading-chart-bg"
    >
      {/* --- GRID --- */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.35]"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <defs>
          <pattern id="tcg-grid" width="5" height="5" patternUnits="userSpaceOnUse">
            <path
              d="M 5 0 L 0 0 0 5"
              fill="none"
              stroke="rgba(245,185,66,0.32)"
              strokeWidth="0.1"
            />
          </pattern>
        </defs>
        <rect width="100" height="100" fill="url(#tcg-grid)" />
      </svg>

      {/* --- Y axis price ticks (right side, faint) --- */}
      <div className="absolute right-2 top-0 bottom-0 hidden sm:flex flex-col justify-between py-10 opacity-25">
        {['1.2840', '1.2810', '1.2780', '1.2750', '1.2720', '1.2690'].map((p) => (
          <span
            key={p}
            className="text-[10px] font-mono text-[hsl(43_92%_56%)] tracking-tight"
          >
            {p}
          </span>
        ))}
      </div>

      {/* --- Animated line charts (3 stacked translating left) --- */}
      <svg
        className="absolute inset-0 w-full h-full opacity-90"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <motion.g
          animate={{ x: [0, -100, 0] }}
          transition={{ duration: 60, ease: 'linear', repeat: Infinity }}
        >
          <polyline
            points={line1}
            fill="none"
            stroke="rgba(245,185,66,0.85)"
            strokeWidth="0.42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </motion.g>
        <motion.g
          animate={{ x: [0, -100, 0] }}
          transition={{ duration: 80, ease: 'linear', repeat: Infinity }}
        >
          <polyline
            points={line2}
            fill="none"
            stroke="rgba(225,29,46,0.65)"
            strokeWidth="0.32"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </motion.g>
        <motion.g
          animate={{ x: [0, -100, 0] }}
          transition={{ duration: 100, ease: 'linear', repeat: Infinity }}
        >
          <polyline
            points={line3}
            fill="none"
            stroke="rgba(108,176,255,0.50)"
            strokeWidth="0.26"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </motion.g>
      </svg>

      {/* --- Animated candlesticks row (drifts L→R slowly) --- */}
      <svg
        className="absolute inset-x-0 bottom-0 w-full h-1/2 opacity-80"
        viewBox="0 0 200 100"
        preserveAspectRatio="none"
      >
        <motion.g
          animate={{ x: [0, 40, 0] }}
          transition={{ duration: 18, ease: 'easeInOut', repeat: Infinity }}
        >
          {candles.map((c, i) => {
            const x = i * 8 + 4;
            const bodyTop = Math.min(c.open, c.close);
            const bodyHeight = Math.max(0.8, Math.abs(c.close - c.open));
            const color = c.bull ? 'rgba(70,200,120,0.9)' : 'rgba(225,80,90,0.9)';
            return (
              <g key={i}>
                {/* wick */}
                <line
                  x1={x}
                  x2={x}
                  y1={c.low}
                  y2={c.high}
                  stroke={color}
                  strokeWidth="0.5"
                />
                {/* body */}
                <rect
                  x={x - 1.6}
                  y={bodyTop}
                  width="3.2"
                  height={bodyHeight}
                  fill={color}
                  rx="0.4"
                />
              </g>
            );
          })}
        </motion.g>
      </svg>

      {/* --- Soft vignette (gentle, lets chart breathe through) --- */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(70% 60% at 50% 40%, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.40) 60%, rgba(0,0,0,0.62) 100%)',
        }}
      />
    </div>
  );
}
